/* @flow */

import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {useRefObserver} from 'shells/utils/hooks/use-ref-observer';
import {Divider, Stack} from 'shells/layout';
import {Link} from 'shells/link';
import {HoverPopover, type HoverPopoverLocationEnum} from 'shells/hover-popover';
import {Popover} from 'shells/popover';

import {LoadingIcon, WarningIcon} from 'shells/icon';
import {PlugErrorState} from 'shells/illustrations';

import {useGetSessionUser} from '../../core/use-get-session-user';

import {AiButtonContent} from '../ai-button-content';

import {useGetPowerAiMonthlyUsage} from './graphql/hooks/use-get-power-ai-monthly-usage';

import './power-ai-usage-popover.css';

type Props = {|
    popoverAnchor: any,
    variant: 'timeline' | 'zoom',
    // Pass in to disable button when monthly response limit has been hit
    buttonRef?: any,
    // Location of the popover - default is top
    location?: HoverPopoverLocationEnum,
    isTooltip?: boolean,
|};

export function PowerAiUsagePopover(props: Props) {
    const {buttonRef, location = 'top'} = props;

    const linkContainerRef = React.useRef(null);

    const {monthlyUsage, isLoading, error} = useGetPowerAiMonthlyUsage();
    const {user} = useGetSessionUser();

    const {isHovered} = useRefObserver(props.popoverAnchor, ['isHovered']);

    // Only show link to users with billing access who are not on Power AI plan or have hit their monthly limit
    const shouldShowBillingLink =
        user &&
        user.permissions.canAccessBilling &&
        (!NutClientConfig.hasPowerAiPlan || (monthlyUsage && monthlyUsage.remaining === 0));

    let title, body;
    switch (props.variant) {
        case 'timeline':
            title = 'Summarize this timeline';
            body = (
                <>
                    Nutshell uses AI to analyze your timeline interactions and provide you with a
                    brief summary.
                </>
            );
            break;
        case 'zoom':
            title = 'Summarize this transcription';
            body = (
                <>
                    Nutshell uses AI to generate a brief summary of your Zoom meeting
                    transcriptions.
                </>
            );
            break;
    }

    const getLimitsContent = () => {
        if (isLoading) {
            return (
                <div className='flex justify-center'>
                    <LoadingIcon size={24} />
                </div>
            );
        }

        if (monthlyUsage) {
            return (
                <div>
                    Your team has <strong>{monthlyUsage.remaining.toLocaleString()}</strong>{' '}
                    {monthlyUsage.remaining === 1 ? 'summary' : 'summaries'} left out of{' '}
                    <strong>{monthlyUsage.limit.toLocaleString()}</strong> per month.
                </div>
            );
        }

        if (error) {
            return (
                <Stack spacing={8} isHorizontal={true} alignCenter={true}>
                    <div styleName='error-svg-container'>
                        <PlugErrorState />
                    </div>
                    <div>Something went wrong fetching this response. Try again in a bit!</div>
                </Stack>
            );
        }
    };

    React.useEffect(() => {
        // If buttonRef is passed in, then we can disable it if no responses remain
        if (buttonRef && buttonRef.current) {
            if (!monthlyUsage || (monthlyUsage && monthlyUsage.remaining === 0)) {
                buttonRef.current.disabled = true;
            } else {
                buttonRef.current.disabled = false;
            }
        }
    }, [buttonRef, monthlyUsage]);

    const popoverContent = (
        <div styleName='power-ai-usage-popover-content'>
            {monthlyUsage && monthlyUsage.remaining === 0 ? (
                <div styleName='no-credits'>
                    <WarningIcon size={15} wrapWithDiv={false} />
                    <div>No credits left</div>
                </div>
            ) : (
                undefined
            )}
            <Stack spacing={12}>
                <div styleName='title'>{title}</div>
                <div>{body}</div>
                <Divider variant='line--horizontal' />
                {getLimitsContent()}
                {shouldShowBillingLink && !props.isTooltip ? (
                    <div ref={linkContainerRef}>
                        <Link
                            variant='button-ai-blue'
                            as={RoutedLink}
                            to='/setup/billing'
                            newTab={true}
                            isFullWidth={true}
                        >
                            <div className='flex justify-center'>
                                <AiButtonContent
                                    buttonText='View Power AI plan'
                                    buttonRef={linkContainerRef}
                                />
                            </div>
                        </Link>
                    </div>
                ) : (
                    undefined
                )}
            </Stack>
        </div>
    );

    return props.isTooltip && isHovered ? (
        <Popover anchor={props.popoverAnchor.current} overlayStyles={{display: 'none'}}>
            {popoverContent}
        </Popover>
    ) : (
        <HoverPopover anchorRef={props.popoverAnchor} location={location}>
            {popoverContent}
        </HoverPopover>
    );
}
